import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export interface Child {
  id: string;
  student_email: string;
  student_name: string;
  grade: string | null;
  created_at: string;
}

export interface TutoringSchedule {
  id: string;
  student_email: string;
  student_name: string | null;
  session_date: string;
  start_time: string;
  end_time: string;
  subject: string;
  topic: string | null;
  status: string;
  instructor_id: string;
}

export interface InstructorProfile {
  id: string;
  first_name: string;
  last_name: string;
}

export interface TestInvitation {
  id: string;
  test_id: string;
  email: string;
  invitation_code: string;
  sent_at: string;
  completed_at: string | null;
  score: number | null;
  instructor_tests: {
    title: string;
    subject: string;
    grade: string;
    topic: string;
    total_questions: number;
    time_limit: number | null;
    description: string | null;
  };
}

/**
 * Shared loader for parent-scoped data (children + their schedules + test invites).
 * Used by the slim parent dashboard and the per-section parent routes.
 */
export function useParentData() {
  const { user } = useAuth();
  const [children, setChildren] = useState<Child[]>([]);
  const [schedules, setSchedules] = useState<TutoringSchedule[]>([]);
  const [instructorProfiles, setInstructorProfiles] = useState<Record<string, InstructorProfile>>({});
  const [testInvitations, setTestInvitations] = useState<TestInvitation[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { data: parentData } = await supabase
        .from("parents")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (!parentData) {
        setChildren([]);
        setSchedules([]);
        setTestInvitations([]);
        return;
      }

      const { data: childrenData } = await supabase
        .from("children")
        .select("*")
        .eq("parent_id", parentData.id)
        .order("created_at", { ascending: false });

      setChildren(childrenData || []);

      if (childrenData && childrenData.length > 0) {
        const childEmails = childrenData.map((c) => c.student_email);

        const { data: schedulesData } = await supabase
          .from("tutoring_schedules")
          .select("*")
          .in("student_email", childEmails)
          .gte("session_date", new Date().toISOString().split("T")[0])
          .order("session_date", { ascending: true })
          .order("start_time", { ascending: true });

        setSchedules(schedulesData || []);

        const instructorIds = [...new Set((schedulesData || []).map((s) => s.instructor_id))];
        if (instructorIds.length > 0) {
          const { data: profileData } = await supabase
            .from("profiles")
            .select("id, first_name, last_name")
            .in("id", instructorIds);
          if (profileData) {
            const map = profileData.reduce((acc, p) => {
              acc[p.id] = p;
              return acc;
            }, {} as Record<string, InstructorProfile>);
            setInstructorProfiles(map);
          }
        }

        const { data: invitationsData } = await supabase
          .from("test_invitations")
          .select(`
            *,
            instructor_tests (
              title, subject, grade, topic, total_questions, time_limit, description
            )
          `)
          .in("email", childEmails)
          .order("sent_at", { ascending: false });

        setTestInvitations(invitationsData || []);
      } else {
        setSchedules([]);
        setTestInvitations([]);
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.email) reload();
  }, [user, reload]);

  return { children, schedules, instructorProfiles, testInvitations, loading, reload };
}
